/**
 * Created by Jerker Nord on 2022-11-02.
 */
import {BenjiKIVSymbol} from "./BenjiKIVSymbol";
import {BenjiKIVSymbolStates} from "../../core/reel/ReelInterfaces";
import {BenjiKIVStatics} from "../BenjiKIVStatics";
import {BenjiKIVGameAssets} from "../BenjiKIVGameAssets";
import {gsap} from "gsap";
import {BenjiKIV} from "../BenjiKIV";

export class BenjiKIVStackedSymbol extends BenjiKIVSymbol {

    private static MULTIPLIER_TEXT_CONTAINER_TOP_OFFSET:PIXI.Point = new PIXI.Point(0, -160);
    private static MULTIPLIER_TEXT_CONTAINER_CENTER_OFFSET:PIXI.Point = new PIXI.Point(0, 0);
    private static MULTIPLIER_TEXT_CONTAINER_BOTTOM_OFFSET:PIXI.Point = new PIXI.Point(0, 160);

    private _multiplierValue:number;
    private _multipliers:PIXI.Sprite[];
    private _deadOverlay:PIXI.Sprite;
    private _isDead:boolean = false;
    private _deadOverlayTL:gsap.core.Timeline;
    private _fadeOutMultiplierTL:gsap.core.Timeline;

    private readonly _multiplierTextContainer:PIXI.Container;
    private readonly _deadOverlayContainer:PIXI.Container;

    constructor() {
        super();
        this._multiplierValue = 1;
        this.isStacked = true;
        this._multiplierTextContainer = new PIXI.Container();
        this._multiplierTextContainer.visible = false;
        this._multiplierTextContainer.position = BenjiKIVStackedSymbol.MULTIPLIER_TEXT_CONTAINER_CENTER_OFFSET;

        this._deadOverlayContainer = new PIXI.Container();
        this._deadOverlayContainer.visible = false;
        this._deadOverlay = new PIXI.Sprite();
        this._deadOverlay.anchor.set(0.5, 0.5);
        this._deadOverlayContainer.addChild(this._deadOverlay);

        this._multipliers = [];
        let tempMulti:PIXI.Sprite;
        for(let i:number = 0; i < BenjiKIVGameAssets.STACKED_MULTIPLIERS.length; i++) {
            tempMulti = new PIXI.Sprite(BenjiKIV.assetLoader.getTextures(BenjiKIVGameAssets.STACKED_MULTIPLIERS[i])[0]);
            tempMulti.anchor.set(0.5, 0.5);
            tempMulti.visible = false;
            this._multiplierTextContainer.addChild(tempMulti);
            this._multipliers.push(tempMulti);
        }

        this._fadeOutMultiplierTL = gsap.timeline({paused : true});
        this._fadeOutMultiplierTL.add(() => {this._multiplierTextContainer.alpha = 1});
        this._fadeOutMultiplierTL.add(gsap.to(this._multiplierTextContainer, {
            duration : 0.15,
            alpha : 0,
            delay : (0.15 / 2)
        }));

        this.addChild(this._deadOverlayContainer);
        this.addChild(this._multiplierTextContainer);
        this.addChild(this._darkOverlay);
    }

    public init(initState:BenjiKIVSymbolStates):BenjiKIVStackedSymbol {
        this.stackedSymbolName = this.symbolName;
        this.symbolName = this.symbolName.slice(0, 2);

        this.getNewStateAnimation(initState);

        return this;
    }

    public getNewStateAnimation(state:BenjiKIVSymbolStates, changeSpeed:number = 0, delay:number = 0, newStateAlpha:number = 1):gsap.core.Timeline {
        //Hide dead overlay on spin out
        if(state == BenjiKIVSymbolStates.SPIN) {
            if(this._isDead == true) {
                if(this._deadOverlayTL && this._deadOverlayTL.isActive()) {
                    this._deadOverlayTL.totalProgress(1);
                }
                this._deadOverlayContainer.visible = false;
                state = BenjiKIVSymbolStates.M_DEAD_SPIN;
                this._fadeOutMultiplierTL.restart();
            }else{
                if (this._multiplierValue > 1){
                    this._fadeOutMultiplierTL.restart();
                }
            }
        }

        return super.getNewStateAnimation(state, changeSpeed, delay, newStateAlpha);
    }

    public getActivateDeadOverlayTL():gsap.core.Timeline {
        this._deadOverlay.texture = BenjiKIV.assetLoader.getTextures(this.symbolName + "/" + this.symbolName + "_dead")[0];

        this._deadOverlayTL = gsap.timeline();
        this._deadOverlayTL.add(() => {this._deadOverlayContainer.alpha = 0}, "+=0.1");
        this._deadOverlayTL.add(() => {this._deadOverlayContainer.visible = true});
        this._deadOverlayTL.add(gsap.to(this._deadOverlayContainer, {duration : 0.6, alpha : 1}));
        this._deadOverlayTL.add(this.getNewStateAnimation(BenjiKIVSymbolStates.M_DEAD, 0));
        return this._deadOverlayTL;
    }

    public updateMultiplier(value:number):gsap.core.Timeline {
        const updateTL:gsap.core.Timeline = gsap.timeline();
        //If quick stop on split and nudge, the values can be in strange order
        if(value <= this._multiplierValue) {
            return updateTL;
        }

        updateTL.add(() => {
            this._multiplierValue = value;
            this._multiplierTextContainer.visible = true;
            this.hideAllMultipliers();
            this._multipliers[value - 2].visible = true;
        });
        return updateTL;
    }

    public updateMultiContainerPosition():void {
        if(this.reelPosition == 2) {
            this.setMultiContainerPosition(BenjiKIVStackedSymbol.MULTIPLIER_TEXT_CONTAINER_BOTTOM_OFFSET);
            BenjiKIV.overlayScatterView.checkOverlayOnTopOfMultiplier(this.reelId, this.reelPosition);
        } else if(this.reelPosition == 6) {
            this.setMultiContainerPosition(BenjiKIVStackedSymbol.MULTIPLIER_TEXT_CONTAINER_TOP_OFFSET);
            BenjiKIV.overlayScatterView.checkOverlayOnTopOfMultiplier(this.reelId, this.reelPosition - 2);
        } else {
            BenjiKIV.overlayScatterView.checkOverlayOnTopOfMultiplier(this.reelId, this.reelPosition - 1);
        }
    }

    private hideAllMultipliers():void {
        for(let i:number = 0; i < this._multipliers.length; i++) {
            this._multipliers[i].visible = false;
        }
    }

    public reset():void {
        super.reset();
        this._multiplierTextContainer.visible = false;
        this._multiplierTextContainer.alpha = 1;
        this._multiplierTextContainer.position = BenjiKIVStackedSymbol.MULTIPLIER_TEXT_CONTAINER_CENTER_OFFSET;
        this._multiplierValue = 1;
        this.hideAllMultipliers();
        this._deadOverlayContainer.visible = false;
        this._isDead = false;
    }

    protected setDarkOverlaySize():void {
        this.darkOverlay.width = BenjiKIVStatics.symbolWidth;
        this.darkOverlay.height = BenjiKIVStatics.symbolHeight * 3;
    }

    public setMultiContainerPosition(newPosition:PIXI.Point):void {
        this._multiplierTextContainer.position = newPosition;
    }

    get isDead():boolean {
        return this._isDead;
    }

    set isDead(value:boolean) {
        this._isDead = value;
    }
}